/**
 * useChainActions Hook
 *
 * Reorders disconnected chains (move left/right).
 * Stored as sprint.chains: { id, order }. Names are not used.
 */

export const useChainActions = (localSprint, setLocalSprint, recordAction) => {
  
  /**
   * Get the current chains array from the sprint, or empty array if not set
   */
  const getChains = () => {
    return localSprint?.chains || [];
  };

  /**
   * Move a chain left (decrease order)
   * @param {string} chainId - The chain's unique identifier
   * @param {Array} currentChainInfo - Current chain info from layout (to know all chains)
   */
  const handleChainMoveLeft = (chainId, currentChainInfo) => {
    moveChain(chainId, -1, currentChainInfo);
  };

  /**
   * Move a chain right (increase order)
   * @param {string} chainId - The chain's unique identifier
   * @param {Array} currentChainInfo - Current chain info from layout (to know all chains)
   */
  const handleChainMoveRight = (chainId, currentChainInfo) => {
    moveChain(chainId, 1, currentChainInfo);
  };

  /**
   * Internal function to move a chain by a delta.
   * Visual neighbor (brace X / effectiveOrder) must match the swap — not stale stored.order.
   */
  const moveChain = (chainId, delta, currentChainInfo) => {
    if (!currentChainInfo || currentChainInfo.length === 0) {
      console.warn('[useChainActions] moveChain called with empty chainInfo');
      return;
    }
    
    // Get existing chains (may be empty on first interaction)
    const existingChains = getChains();

    // Order by what the user sees (same basis as ChainHeaders arrows / swapChainsAndCompact)
    const orderedChains = [...currentChainInfo]
      .sort((a, b) => {
        if (a.effectiveOrder !== undefined && b.effectiveOrder !== undefined) {
          return a.effectiveOrder - b.effectiveOrder;
        }
        return (a.position?.x ?? 0) - (b.position?.x ?? 0);
      })
      .map((info, idx) => ({
        id: info.id,
        order: idx,
      }));
    
    // Find current index in visual order
    const currentIndex = orderedChains.findIndex(c => c.id === chainId);
    if (currentIndex === -1) {
      console.warn('[useChainActions] Chain not found:', chainId);
      return;
    }
    
    const newIndex = currentIndex + delta;
    if (newIndex < 0 || newIndex >= orderedChains.length) {
      console.warn('[useChainActions] Invalid move:', { currentIndex, newIndex, delta });
      return;
    }
    
    // Capture old state for undo
    const oldChains = [...existingChains];
    
    // Create new chains array with swapped orders (contiguous 0..n-1)
    const updatedChains = orderedChains.map((c, idx) => {
      if (idx === currentIndex) {
        return { ...c, order: newIndex };
      } else if (idx === newIndex) {
        return { ...c, order: currentIndex };
      }
      return { ...c, order: idx };
    });
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      return { ...prev, chains: updatedChains };
    });

    // Record for undo
    if (recordAction) {
      const direction = delta > 0 ? 'right' : 'left';

      recordAction({
        type: 'MOVE_CHAIN',
        description: `Move chain ${direction}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, chains: oldChains };
          });
        },
        redo: () => {
          moveChain(chainId, delta, currentChainInfo);
        },
      });
    }
  };

  /**
   * Get the chain order array for layout
   * Returns array of chain IDs in order
   */
  const getChainOrder = () => {
    const chains = getChains();
    if (chains.length === 0) return null;
    
    // Sort by order and return IDs
    return [...chains]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(c => c.id);
  };

  return {
    getChains,
    getChainOrder,
    handleChainMoveLeft,
    handleChainMoveRight,
  };
};

export default useChainActions;
